/* eslint-disable warp-drive/no-external-request-patterns, warp-drive/no-legacy-request-patterns */
// Extending From
import Service from '@ember/service';
// Ember Imports
import config from '@linc-technologies/linced-common/utils/config';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import md5 from 'blueimp-md5';
// Internal Imports
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';
import { isSafari } from '@linc-technologies/linced-common/utils/platform';

export default class MediaService extends Service {
  // Services
  @service
  session;

  @service
  store;

  @service
  toaster;

  async onMediaCreateOrLoad(media) {
    // Special case for pdfs
    if (
      media.mimeType === 'application/pdf' &&
      media.mediaSource === MediaSource.INTERNAL
    ) {
      const uri = await this.getFileBlobUri(media.id);
      media.set('blobUri', uri);
    }

    // This shouldn't be run for non images.
    if (media.mediaType !== MediaType.IMAGE) {
      return;
    }
    // If image url is already filled out don't recompute.
    if (!isEmpty(media.imageUrl)) {
      return;
    }

    // If embedded from Google Drive, don't attempt to blobify.
    if (media.mediaSource === MediaSource.GOOGLE_DRIVE) {
      return;
    }

    const uri = await this.getImageBlobUri(media.id);
    media.set('imageUrl', uri);
  }

  async onResourceLoadOrUpdate(
    resource,
    mediaId,
    urlProp,
    hasGravatar,
    originalSize,
  ) {
    let gravatarId = '';
    if (hasGravatar) {
      gravatarId = resource.id;
      resource.set(
        urlProp,
        `https://gravatar.com/avatar/${md5(gravatarId)}?d=identicon&f=y`,
      );
    }
    const rendition = originalSize ? null : 'avatar';
    const url = await this.getImageBlobUri(mediaId, gravatarId, rendition);
    if (isEmpty(url)) {
      return;
    }
    resource.set(urlProp, url);
  }

  onDestroy(blobUrl) {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  async getImageBlobUri(id, resourceId, rendition, accept = '') {
    if (isEmpty(id)) {
      if (isEmpty(resourceId)) {
        return '';
      }
      return `https://gravatar.com/avatar/${md5(resourceId)}?d=identicon&f=y`;
    }

    try {
      const tokenWrapper = await this.session.getToken();
      let headers = {
        Accept: 'image/webp,image/png,image/*;q=0.8',
        Authorization: `Bearer ${tokenWrapper.token}`,
      };
      if (!isEmpty(accept)) {
        headers.Accept = accept;
      }

      let opts = {
        headers,
      };

      if (rendition) {
        opts.queryParams = {
          rendition,
        };
      }
      const response = await this.fetchFileBlob(id, opts);
      const blob = await response;
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error(e);
    }
  }

  async getImageBase64(id, rendition) {
    try {
      const tokenWrapper = await this.session.getToken();
      const headers = {
        Accept: 'image/jpeg,image/png,image/gif,image/bmp',
        Authorization: `Bearer ${tokenWrapper.token}`,
      };

      const response = await this.fetchFileBlob(id, {
        headers,
        queryParams: {
          rendition,
        },
      });
      const blob = await response;
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getFile(id) {
    if (isEmpty(id)) {
      return '';
    }

    try {
      const tokenWrapper = await this.session.getToken();
      const headers = {
        Authorization: `Bearer ${tokenWrapper.token}`,
      };

      const response = await this.fetchFileBlob(id, {
        headers,
      });
      return await response;
    } catch (e) {
      console.error(e);
    }
  }

  async getFileBlobUri(id) {
    if (isEmpty(id)) {
      return '';
    }

    try {
      const tokenWrapper = await this.session.getToken();
      const headers = {
        Authorization: `Bearer ${tokenWrapper.token}`,
      };

      const response = await this.fetchFileBlob(id, {
        headers,
      });
      const blob = await response;
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error(e);
    }
  }

  async fetchFileBlob(id, { headers = {}, queryParams = {} } = {}) {
    const queryString = new URLSearchParams(queryParams).toString();
    let fetchHeaders = new Headers(headers);

    // Construct the query string
    let url = `${config.APP.apiHost}/media/${config.APP.apiPath}/file/${id}${
      queryString ? `?${queryString}` : ''
    }`;

    // If this is safari we need to manually get the final redirected url for media
    // as there is a bug with cors redirects on safari that causes them to fail.
    // https://bugs.webkit.org/show_bug.cgi?id=98838
    if (isSafari()) {
      let locationReqUrl = `${config.APP.apiHost}/media/${
        config.APP.apiPath
      }/file/${id}/:location${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(locationReqUrl, {
        method: 'GET',
        headers: fetchHeaders,
      });

      const data = await response.json();
      url = data.location;
      fetchHeaders.delete('Authorization');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: fetchHeaders,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Return the response as a Blob
    return response.blob();
  }

  async getFileLocation(id, rendition) {
    const tokenWrapper = await this.session.getToken();
    const headers = new Headers({
      Accept: 'image/jpeg,image/png,image/gif,image/bmp',
      Authorization: `Bearer ${tokenWrapper.token}`,
    });

    const queryString = new URLSearchParams({ rendition }).toString();

    let locationReqUrl = `${config.APP.apiHost}/media/${
      config.APP.apiPath
    }/file/${id}/:location${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(locationReqUrl, {
      method: 'GET',
      headers: headers,
    });

    const data = await response.json();
    return data.location;
  }

  async handleImageUpload(
    controller,
    file,
    upload,
    sharedWith,
    schoolId,
    personId,
    mediaIdProp,
    mediaUrlProp,
  ) {
    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);

    controller.set(mediaUrlProp, '');

    try {
      let mediaRecord = await this.store.createRecord('media', {
        schoolId,
        personId,
        sharedWith: sharedWith,
        mediaType: MediaType.IMAGE,
        mediaSource: mediaSource,
        mimeType: file.type,
        fileName: file.name,
        storageKey: key,
        fileSize: file.size,
      });
      await mediaRecord.save();

      controller.set(mediaIdProp, mediaRecord.id);
      await this.onMediaCreateOrLoad(mediaRecord);
      controller.set(mediaUrlProp, mediaRecord.imageUrl);
    } catch (e) {
      console.error(e);
      this.toaster.error(`Error uploading image!`);
    }

    controller.set('uploading', false);
  }

  async createZip(mediaIds, schoolId) {
    if (isEmpty(mediaIds)) {
      return;
    }
    if (isEmpty(schoolId)) {
      schoolId = this.session.activeSchool;
    }
    const tokenWrapper = await this.session.getToken();
    const headers = {
      Accept: 'application/zip',
      Authorization: `Bearer ${tokenWrapper.token}`,
      'Content-Type': 'application/json',
    };
    let data = {
      mediaIds,
      schoolId,
    };
    let url = `${config.APP.apiHost}/media/${config.APP.apiPath}/zip`;
    let response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let jsonResponse = await response.json();

    return jsonResponse.processMonitorId;
  }

  async getZipBlobUri(storageKey, schoolId, filename) {
    if (isEmpty(storageKey)) {
      return '';
    }

    try {
      const tokenWrapper = await this.session.getToken();
      const headers = {
        Authorization: `Bearer ${tokenWrapper.token}`,
      };

      let queryParams = { schoolId: schoolId, filename: filename };
      const queryString = new URLSearchParams(queryParams).toString();
      let fetchHeaders = new Headers(headers);

      // Construct the query string
      let url = `${config.APP.apiHost}/media/${
        config.APP.apiPath
      }/zip/${storageKey}${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: fetchHeaders,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error(e);
    }
  }
}
